'use client';
import { createDebug } from '@shared/debug';
import { AltenarWidgetType, useAltenarSDK } from '@shared/hooks';
import { useEffect, useRef, type ComponentProps } from 'react';

type AltenarWidgetBonusesProps = ComponentProps<'div'>;

/**
 * A component that creates a free bets widget using the Altenar SDK.
 * @param props - The props for the component.
 * @returns A div element with the free bets widget.
 */
function AltenarWidgetBonuses({ ...props }: AltenarWidgetBonusesProps) {
  const { createWidget } = useAltenarSDK();
  const debug = createDebug({ isEnabled: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<{ remove: () => void; update: (props: unknown) => void } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    if (container.hasChildNodes()) return;
    createWidget(AltenarWidgetType.WBonuses, container, {}, {})
      .then(widget => {
        widgetRef.current = widget;
      })
      .catch(error => {
        debug.error('Failed to create free bets widget:', error);
      });

    return () => {
      if (widgetRef.current) {
        try {
          widgetRef.current.remove();
          widgetRef.current = null;
        } catch (error) {
          debug.error('Error removing widget:', error);
        }
      }
    };
  }, [createWidget, debug]);

  return <div ref={containerRef} {...props} />;
}

export { AltenarWidgetBonuses };
